import { Signal } from '@angular/core';
import { PathKind, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';
import { spanEpoch } from './chronologically-ordered.rule';

/** Options of the {@link notEarlierThan} rule. */
export interface NotEarlierThanConfig {
    /** builds the message from the limit the value may not fall before */
    message?: (limit: string) => string;
    /**
     * Whether the limit itself is an acceptable value. Defaults to true — the limit is the first
     * instant that is still in bounds.
     *
     * Set false where the two instants may not name the same moment: a value that must come strictly
     * after the limit rather than merely not before it. That is the difference between two spans
     * meeting end to end, which is ordinarily what a caller wants, and two spans that must be
     * separated by something — however little.
     */
    allowEqual?: boolean;
}

/**
 * Signal-forms schema rule rejecting an instant that falls before a limit which is not itself part
 * of the form — something read from outside it, rather than a sibling field the same schema also
 * validates. The timesheet page uses it to keep a day's `startOfWork` from reaching back into a day
 * that was already submitted, where the limit is the latest `endOfWork` any submitted timesheet
 * accounted for.
 *
 * The limit is a signal rather than a plain value for the same reason `forbiddenCharacters`'
 * pattern is: the rule makes no assumption about running inside an injection context, and stays
 * trivially testable. Callers hand it whatever produces the current limit — most likely
 * `store.selectSignal(...)` — without this rule needing to know where it comes from.
 *
 * Both sides are parsed with {@link spanEpoch}, the same parser every other span rule in this
 * folder uses, so a value this rule and one of those would disagree about does not exist. An unset
 * or unparsable value is passed over on either side: whether an empty value is acceptable is a
 * `required` question, not this one's, and a limit that is absent — nothing submitted yet, or no
 * timesheet selected at all — leaves nothing to compare against.
 *
 * Whether the limit itself is in bounds is the caller's to say, through
 * {@link NotEarlierThanConfig.allowEqual}. It is by default, which is what the timesheet wants: a
 * new day may start at the exact instant the previous one ended.
 *
 * No `validateTree` and no collection-level summary error here, unlike the span rules that share
 * this folder: this marks one editable leaf field, not a collection some of whose fields might all
 * be readonly and so need a fallback place to report the conflict.
 *
 * @param path the string path to validate
 * @param limit the instant the value may not fall before, as a signal
 * @param config optional message factory
 */
export function notEarlierThan<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
    limit: Signal<string>,
    config?: NotEarlierThanConfig,
): void {
    validate(path, (context) => {
        const valueEpoch = spanEpoch(context.value());
        const limitEpoch = spanEpoch(limit());
        if (valueEpoch === undefined || limitEpoch === undefined) {
            return undefined;
        }

        const inBounds = config?.allowEqual === false ? valueEpoch > limitEpoch : valueEpoch >= limitEpoch;
        return inBounds ? undefined : { kind: 'earlierThanLimit', message: config?.message?.(limit()) ?? '' };
    });
}
